import { useFormField } from "uiShadcn/form";
import * as Sentry from "@sentry/nextjs";
import { useFormStatusContext } from "forms/contexts/useFormStatusContext";
import type { FormCvProps } from "forms/fields/Cv/types";
import { useFormErrors } from "forms/hooks/useFormErrors";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { getFileExtensionFromName } from "utils/helpers/getFileExtensionFromName";
import { uploadFileClient } from "utils/uploadFileClient";
interface UseCvProps extends Required<Pick<FormCvProps, "onChange" | "validators">> {}
export default function useCv({
  onChange,
  validators
}: UseCvProps) {
  const {
    formatMessage
  } = useIntl();
  const [isUploading, setIsUploading] = useState(false);
  const {
    error,
    name
  } = useFormField();
  const {
    setError,
    clearErrors
  } = useFormContext();
  const {
    setLoading
  } = useFormStatusContext();
  const {
    handleFileUploadError
  } = useFormErrors();
  const extensions = validators?.find(validator => validator?.validatorType === "filenameExtensionValidator");
  const extensionsArray = extensions?.oneOf as string[] ?? [];
  const allowedExtensions = extensionsArray.join(", ");
  const maxSizeValidator = validators?.find(validator_0 => validator_0?.validatorType === "fileMaxSizeValidator");
  const maxSize = Number(maxSizeValidator?.size ?? 0);
  const maxSizeInMB = Number(maxSize) / 1024 / 1024;
  const onChangeHandler = async (file: File | null) => {
    try {
      if (error) {
        clearErrors(name);
      }
      if (!file) {
        onChange([]);
        return;
      }
      if (file.size > maxSize) {
        setError(name, {
          type: "invalidFileSize",
          message: formatMessage({
            id: "fileSize"
          }, {
            size: maxSizeInMB
          })
        });
        return;
      }
      const fileExt = getFileExtensionFromName(file?.name);
      if (!extensionsArray.includes(fileExt)) {
        setError(name, {
          type: "invalidFileExtension",
          message: formatMessage({
            id: "forbiddenExtension"
          })
        });
        return;
      }
      setIsUploading(true);
      setLoading(true);
      const sendFileResult = await uploadFileClient(file);
      if (sendFileResult?.isSuccess && sendFileResult?.data?.fileid) {
        onChange([{
          id: sendFileResult.data.fileid
        }]);
        return;
      }
      if (!sendFileResult.isSuccess && sendFileResult.error) {
        onChange([]);
        handleFileUploadError({
          error: sendFileResult.error,
          setError,
          name,
          maxSizeInMB
        });
        return;
      }
    } catch (err) {
      Sentry.withScope(scope => {
        scope.setTag("feature", "send-cv");
        scope.setContext("File", {
          maxSize,
          fileSize: file?.size,
          fileType: file?.type,
          fileName: file?.name
        });
        Sentry.captureException(err);
      });
      setError(name, {
        type: "sendFileError",
        message: formatMessage({
          id: "fileUploadError"
        })
      });
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };
  return {
    onChangeHandler,
    maxSizeInMB,
    allowedExtensions,
    isUploading
  };
}